// Проводной протокол: то же самое, что `crates/protocol` на стороне Rust.
//
// Два канала поверх одного WebSocket: бинарные фреймы — PCM с 16-байтовым
// заголовком, текстовые — JSON (хендшейк и управляющие сообщения). Ни magic,
// ни поля типа в заголовке нет: разделение делает сам WebSocket.

/** Длина заголовка. Совпадает с `protocol::HEADER_LEN`. */
export const HEADER_LEN = 16;
/** Размер одного сэмпла: f32. Совпадает с `protocol::SAMPLE_LEN`. */
export const SAMPLE_LEN = 4;

/**
 * Порядок байт машины.
 *
 * Протокол всегда little-endian. `DataView` умеет писать в любом порядке, а вот
 * `Float32Array` — только в порядке машины, и именно им worklet пишет сэмплы
 * прямо в буфер, чтобы не копировать блок на каждом кадре. На big-endian такой
 * буфер уехал бы в сеть перевёрнутым, поэтому проверку делаем один раз на
 * старте и честно отказываемся, а не портим звук молча.
 */
export const IS_LITTLE_ENDIAN = new Uint8Array(new Uint32Array([1]).buffer)[0] === 1;

/** Разобранный бинарный кадр. */
export interface Frame {
  sequence: number;
  samples: Float32Array;
}

/**
 * Записать заголовок в начало буфера, где сэмплы уже лежат начиная с байта 16.
 *
 * Это горячий путь: буферы приезжают из worklet'а захвата уже с оставленным
 * местом под заголовок, поэтому отправка не копирует ни одного сэмпла.
 */
export function writeHeader(buffer: ArrayBuffer, sequence: number, samples: number): void {
  const view = new DataView(buffer, 0, HEADER_LEN);
  view.setUint32(0, sequence >>> 0, true);
  view.setUint32(4, samples >>> 0, true);
  view.setUint32(8, 0, true); // flags: ни клиент, ни сервер их не выставляют
  view.setUint32(12, 0, true); // reserved
}

/**
 * Собрать кадр целиком.
 *
 * Не горячий путь — нужен тестам и редким служебным отправкам. В потоке
 * используется [`writeHeader`] поверх буфера из пула.
 */
export function encodeFrame(sequence: number, samples: Float32Array): ArrayBuffer {
  const buffer = new ArrayBuffer(HEADER_LEN + samples.length * SAMPLE_LEN);
  writeHeader(buffer, sequence, samples.length);

  const view = new DataView(buffer);
  for (let i = 0; i < samples.length; i += 1) {
    view.setFloat32(HEADER_LEN + i * SAMPLE_LEN, samples[i]!, true);
  }
  return buffer;
}

/**
 * Проверить кадр и вернуть число сэмплов, ничего не копируя.
 *
 * Проверки те же три, что и у `decode_frame`: короче заголовка, полезная
 * нагрузка не кратна четырём, объявленное число сэмплов не совпало с
 * фактическим. Сервер такого не присылает, но молча съесть испорченный кадр
 * хуже, чем сказать о нём.
 *
 * Это горячий путь приёма: сэмплы остаются лежать в том же буфере, и worklet
 * вывода прочитает их видом, а не копией.
 */
export function frameSampleCount(buffer: ArrayBuffer): number {
  if (buffer.byteLength < HEADER_LEN) {
    throw new Error(`кадр короче заголовка: ${buffer.byteLength} < ${HEADER_LEN}`);
  }

  const payload = buffer.byteLength - HEADER_LEN;
  if (payload % SAMPLE_LEN !== 0) {
    throw new Error(`полезная нагрузка не кратна ${SAMPLE_LEN}: ${payload}`);
  }

  const actual = payload / SAMPLE_LEN;
  const declared = new DataView(buffer).getUint32(4, true);
  if (actual !== declared) {
    throw new Error(`объявлено ${declared} сэмплов, а пришло ${actual}`);
  }
  return actual;
}

/**
 * Разобрать кадр целиком.
 *
 * Не горячий путь: нужен тестам и разбору вручную. В потоке используется
 * [`frameSampleCount`] — он не копирует сэмплы.
 */
export function decodeFrame(buffer: ArrayBuffer): Frame {
  const count = frameSampleCount(buffer);
  const view = new DataView(buffer);

  const samples = new Float32Array(count);
  for (let i = 0; i < count; i += 1) {
    samples[i] = view.getFloat32(HEADER_LEN + i * SAMPLE_LEN, true);
  }
  return { sequence: view.getUint32(0, true), samples };
}

// --- текстовый канал ---------------------------------------------------------

/** Версия протокола, которую понимает сервер. Сверяется мажорная часть. */
export const PROTOCOL_VERSION = "1.0";

/** Настройки сессии. Все поля необязательны — у сервера на каждое есть умолчание. */
export interface SessionConfig {
  model_id?: string;
  input_sample_rate?: number;
  output_sample_rate?: number;
  block_size?: number;
  pitch_shift?: number;
  block_ms?: number;
  extra_context_ms?: number;
  crossfade_ms?: number;
  sola_search_ms?: number;
  f0_threshold?: number;
  input_gain_db?: number;
  highpass_hz?: number;
  gate_threshold_db?: number | null;
  gate_attack_ms?: number;
  gate_release_ms?: number;
  gate_hold_ms?: number;
  vad_mode?: string | null;
  vad_threshold?: number | null;
  vad_release_ms?: number;
  rnnoise?: boolean;
  index_ratio?: number;
  protect?: number;
}

export interface ClientHello {
  version: string;
  auth_token: string;
  session: SessionConfig;
}

export interface ServerInfo {
  version: string;
  gpu?: string | null;
  ort_version?: string | null;
  max_block_size?: number | null;
}

export interface SessionAccepted {
  session_id: string;
  input_sample_rate: number;
  output_sample_rate: number;
  model_sr?: number | null;
  f0_conditioned?: boolean;
  applied?: SessionConfig | null;
  voices?: string[];
  server_info: ServerInfo;
}

/**
 * Почему сервер отказал — машиночитаемо. Зеркало `RejectCode` в протоколе.
 *
 * Решения принимаются по коду, а не по тексту: `reason` показывают человеку
 * дословно, но разбирать его в двух языках значит однажды разъехаться молча.
 * `undefined` — сервер старее кодов.
 */
export type RejectCode = "unauthorized" | "forbidden" | "bad_request";

/** Что сервер прислал текстовым фреймом. */
export type ServerText =
  | { kind: "hello-ok"; accepted: SessionAccepted }
  | { kind: "hello-error"; reason: string; code?: RejectCode }
  | { kind: "ack-ok" }
  | { kind: "ack-error"; reason: string }
  | { kind: "unknown"; raw: string };

/**
 * Понять, что за текстовое сообщение пришло.
 *
 * Ловушка, стоящая отдельного теста: успешный хендшейк и подтверждение команды
 * дают одинаковый `{"status":"ok"}`. Различает их наличие `session_id`.
 * Ошибку разводим по тому, ждём ли мы ещё хендшейк: до него любой отказ
 * относится к нему.
 */
export function classifyText(raw: string, awaitingHello: boolean): ServerText {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { kind: "unknown", raw };
  }

  if (typeof parsed !== "object" || parsed === null) {
    return { kind: "unknown", raw };
  }
  const message = parsed as Record<string, unknown>;

  if (message["status"] === "ok") {
    return typeof message["session_id"] === "string"
      ? { kind: "hello-ok", accepted: message as unknown as SessionAccepted }
      : { kind: "ack-ok" };
  }

  if (message["status"] === "error") {
    const reason = typeof message["reason"] === "string" ? message["reason"] : raw;
    if (!awaitingHello) return { kind: "ack-error", reason };

    const raw_code = message["code"];
    const code =
      raw_code === "unauthorized" || raw_code === "forbidden" || raw_code === "bad_request"
        ? raw_code
        : undefined;
    return code === undefined
      ? { kind: "hello-error", reason }
      : { kind: "hello-error", reason, code };
  }

  return { kind: "unknown", raw };
}

/** Команда смены настроек на лету. Поля, которых нет, сервер не трогает. */
export interface SettingsUpdate {
  pitch_shift?: number;
  index_ratio?: number;
  protect?: number;
  f0_smoothing?: number;
  rnnoise?: boolean;
}

export function setCommand(update: SettingsUpdate): string {
  return JSON.stringify({ cmd: "set", ...update });
}
